// The adaptive playout clock: render time runs at real speed, 2.0 s behind the newest frame.
//
//   buffer ahead   rate                     what the viewer sees
//   1.5–2.5 s      1.0                      normal
//   < 1.5 s        0.9                      the buffer quietly refills after a hiccup
//   < 0.5 s        eases to 0.5, then 0     cats slow; "thin", and "lost" once empty for 3 s
//   > 2.5 s        1.15 until back at 2.0   an unnoticeable catch-up
//   > 6 s, or a phase change inside a gap   jump to newest − 2 s (never replay missed time fast)
//
// Between races frames come once a second, so "newest" is extended by the time since the newest frame
// arrived, up to one frame interval. That keeps the buffer steady at either frame rate.

export const DELAY_MS = 2000;
export const JUMP_AHEAD_MS = 6000;
export const LOST_AFTER_MS = 3000;
const RATE_EASE_MS = 300;

export type Health = 'ok' | 'thin' | 'lost';

/** What the clock needs to know about the buffer. The race timeline provides it. */
export interface PlayoutBuffer {
  newestT(): number;
  cadenceMs(): number;
  jumpTarget(renderT: number): number | null;
}

export interface PlayoutStep {
  renderT: number;
  health: Health;
  /** Set when render time jumped forward instead of playing; events in between should be skipped. */
  jumpedFrom: number | null;
}

export class PlayoutClock {
  renderT = Number.NaN;
  rate = 1;
  health: Health = 'ok';
  private catchingUp = false;
  private emptyMs = 0;
  private lastNewest = Number.NaN;
  private sinceNewest = 0;

  /** Buffer ahead of render time, ms, as of the last step. */
  ahead = 0;

  /** Advances by dtMs of source time (wall time × replay speed). */
  advance(dtMs: number, buf: PlayoutBuffer): PlayoutStep {
    const newest = buf.newestT();
    if (Number.isNaN(newest)) return { renderT: this.renderT, health: this.health, jumpedFrom: null };

    if (newest !== this.lastNewest) { this.lastNewest = newest; this.sinceNewest = 0; } else this.sinceNewest += dtMs;
    const head = newest + Math.min(this.sinceNewest, buf.cadenceMs());

    if (Number.isNaN(this.renderT)) {
      this.renderT = head - DELAY_MS;
      this.ahead = DELAY_MS;
      return { renderT: this.renderT, health: this.health, jumpedFrom: null };
    }

    let jumpedFrom: number | null = null;
    const gapEnd = buf.jumpTarget(this.renderT);
    if (head - this.renderT > JUMP_AHEAD_MS || gapEnd !== null) {
      jumpedFrom = this.renderT;
      this.renderT = Math.max(gapEnd ?? -Infinity, head - DELAY_MS);
      this.rate = 1;
      this.catchingUp = false;
    } else {
      const ahead = head - this.renderT;
      if (ahead > 2500) this.catchingUp = true;
      if (ahead <= DELAY_MS) this.catchingUp = false;
      let target = 1;
      if (this.catchingUp) target = 1.15;
      else if (ahead < 50) target = 0;
      else if (ahead < 500) target = 0.5;
      else if (ahead < 1500) target = 0.9;
      this.rate += (target - this.rate) * Math.min(1, dtMs / RATE_EASE_MS);
      if (target === 0 && this.rate < 0.05) this.rate = 0;
      this.renderT = Math.min(head, this.renderT + dtMs * this.rate);
    }

    this.ahead = head - this.renderT;
    if (this.ahead < 100) this.emptyMs += dtMs; else this.emptyMs = 0;
    if (this.emptyMs >= LOST_AFTER_MS) this.health = 'lost';
    else if (this.ahead < 500) this.health = this.health === 'lost' ? 'lost' : 'thin';
    else if (this.ahead >= 1000) this.health = 'ok';
    return { renderT: this.renderT, health: this.health, jumpedFrom };
  }
}
